/**
 * Popover — a floating **panel**, and the third caller of `placement.ts` (`KLQ-20260910-001`
 * §B).
 *
 * **The condition under which a third floater was allowed to exist**, written here so the next
 * reader does not have to reconstruct it. There are two already, and both are lists:
 * `MenuPopup` is a list of *actions* and `ListboxPopup` a list of *options*, so both own roving
 * focus, both close on choosing, and both answer `ArrowDown` by moving a selection. A filter
 * panel is none of that — it is a **form**: eight fields, Tab between them, and choosing a
 * value in one of them is the middle of the task rather than the end of it. Bending either list
 * into a form would have meant disabling the arrow keys, disabling close-on-choose and
 * disabling the item model, which is not reuse, it is a third component wearing another one's
 * name.
 *
 * So the rule, in one line: **a floater that contains form controls is this one; a floater that
 * contains choices is one of those two.** A fourth needs a sentence as short as that one.
 *
 * WHAT IT DOES NOT DO, DELIBERATELY
 *
 * 1. **It does not close when something inside it changes.** Filtering is iterative — pick a
 *    status, look at the list, add a person — and a panel that shut on every change would make
 *    the second thought cost two clicks. Only Escape, a press outside, and the owner close it.
 * 2. **It does not close on scroll.** `MenuPopup` does, and is right to: a menu is a moment.
 *    This is a panel somebody works in while reading the list behind it, and the list is the
 *    thing that scrolls. It re-measures instead, so the box follows its trigger rather than
 *    abandoning it.
 * 3. **It does not trap focus.** It is a non-modal dialog: Tab walks out of the end of it and
 *    on into the page, which is what a person expects of a panel that does not dim what is
 *    behind it. The owner is what puts focus back on the trigger when it closes.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { Placement } from "./placement";
import { place } from "./placement";
import styles from "./Popover.module.css";

/** Why it closed. The owner needs the difference to decide about focus — see `FilterBar`. */
export type PopoverCloseReason = "dismissed" | "moved-on";

export interface PopoverProps {
  /**
   * The trigger. A live element rather than a `DOMRect`, because this box re-measures on
   * scroll and on resize, and a rect captured once is a rect that is wrong the first time the
   * list behind moves.
   */
  readonly anchor: HTMLElement | null;
  /** Which edge of the panel lines up with which edge of the trigger. */
  readonly align?: "start" | "end";
  /** The panel's accessible name. */
  readonly label: string;
  readonly onClose: (reason: PopoverCloseReason) => void;
  readonly children: ReactNode;
}

export function Popover({
  anchor,
  align = "end",
  label,
  onClose,
  children,
}: PopoverProps) {
  const panel = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  // Reached through a ref for the reason `Drawer` states: every caller passes an inline arrow,
  // and an effect that depended on it would re-run — and re-steal focus — on every render.
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  const measure = useCallback(() => {
    const box = panel.current?.getBoundingClientRect();
    if (box === undefined || anchor === null) return;
    const next = place(
      { kind: "element", rect: anchor.getBoundingClientRect(), align },
      box,
    );
    // Same numbers, same object — an unguarded `setPlacement` in a layout effect that also
    // runs on scroll is a render loop, not a measurement. `MenuPopup` learned this first.
    setPlacement((current) =>
      current !== null && current.top === next.top && current.left === next.left
        ? current
        : next,
    );
  }, [anchor, align]);

  useLayoutEffect(measure);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      // The trigger is outside this element and must not count as outside, or pressing it to
      // close would close on the way down and re-open on the click.
      if (target.closest("[data-popover]") !== null) return;
      if (anchor?.contains(target) === true) return;
      close.current("moved-on");
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // The panel holds `<select>`s. A native dropdown that is open eats Escape itself and
      // never reaches here, which is the behaviour we want — Escape closes the dropdown
      // first, the panel second.
      event.stopPropagation();
      close.current("dismissed");
    };
    // Capture on scroll, so a scroll inside the list behind is seen and the box follows.
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchor, measure]);

  // Focus enters the panel itself, not its first field: a filter panel is read before it is
  // typed into, and landing on the first dropdown means a stray keystroke changes a filter.
  // The container is focusable so Escape has somewhere to be heard from.
  useEffect(() => {
    if (placement === null) return;
    panel.current?.focus();
  }, [placement]);

  return createPortal(
    <div
      ref={panel}
      data-popover
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={styles.panel}
      style={
        placement === null
          ? // Measured before it is seen: drawn off-screen for one frame rather than at 0,0,
            // where it would flash in the corner of every screen it opens on.
            { visibility: "hidden", top: 0, left: 0 }
          : { top: `${String(placement.top)}px`, left: `${String(placement.left)}px` }
      }
    >
      {children}
    </div>,
    document.body,
  );
}
